// src/lib/coverage/surveyScoring.ts
// ============================================================
// THE SELF-REPORTED SCORE, DERIVED FROM THE ACTUAL SEEDED ANSWERS
//
// The override sentence claims a client rated an area N of 5. That claim has to survive the
// question "which survey said that?", so N is computed here from the 37 answers in
// services/demoSurveyResponses and from nothing else.
//
// WHAT THIS REPLACED, and why it had to go: an earlier pass mirrored
// CC_MEASURE_RATIONALES.score1to5. Those are a USE CASE's own measure ratings, not ACME's
// self-assessment of ACME. The sentence read plausibly and was not true of any survey.
//
// ─────────────────────────────────────────────────────────────────────────────
// THE SCALE. `1 + 4 x (index / (options - 1))`, so the worst option is 1 and the best is 5
// regardless of how many options a question carries. A module's score is the mean of its
// scorable questions, to one decimal, on the same 1-to-5 scale the documents produce.
//
// THE DERIVATION IS MODULE-LEVEL, and that is a real limitation rather than a shortcut.
// No seeded question carries a measure binding, so nothing maps an answer to a measure. One
// score per module is applied to every measure that module binds. The override copy says
// "your <module> responses rated this area", never "you answered", because the survey
// cannot support the narrower claim.
// ─────────────────────────────────────────────────────────────────────────────
//
// EXCLUSIONS ARE THE POINT. Of 37 questions, 21 produce a score. The other 16 are excluded
// rather than forced onto the scale, because a number nobody supplied is worse than no
// number: it would be indistinguishable on screen from one a client actually gave.
// ============================================================

import {
  DEMO_SURVEY_QUESTIONS,
  type DemoSurveyModuleId,
  type DemoSurveyQuestion,
} from '../../services/demoSurveyResponses';

/** Why a question yields no score. Recorded so an exclusion can be argued with. */
export type ExclusionReason =
  | 'pick-list' // options are alternatives, not a ladder
  | 'multi-select' // a count of selections is not a level
  | 'unvalenced' // ordered, but neither end is better
  | 'not-a-capability' // ordered and valenced, but about the market, not the organization
  | 'trend-not-level' // measures direction of travel
  | 'module-unscored'; // the whole module is unscorable, see MODULE_POLICY

export type QuestionPolicy =
  | { score: true; orientation: 'ascending' | 'descending'; note?: string }
  | { score: false; reason: ExclusionReason };

const asc = (note?: string): QuestionPolicy => ({ score: true, orientation: 'ascending', note });
const desc = (note?: string): QuestionPolicy => ({ score: true, orientation: 'descending', note });
const skip = (reason: ExclusionReason): QuestionPolicy => ({ score: false, reason });

/**
 * Per-question policy. AUTHORED, because the seeded data carries no orientation metadata:
 * nothing in a question says whether its first option is the best or the worst one.
 *
 * Every exclusion carries its reason. Read them before adding a question to the scored set.
 */
export const QUESTION_POLICY: Readonly<Record<string, QuestionPolicy>> = {
  // ---- Strategic Priorities. See MODULE_POLICY: this module is unscored as a whole. ----
  strat_1: skip('pick-list'), // its options ARE the measures the module binds
  strat_2: desc(), // Critical > High > Moderate > Low
  strat_3: skip('multi-select'),
  strat_4: asc('targets a maturity level, so it reads as ambition rather than current state'),
  strat_5: desc(), // CEO/Board > CDO+CFO > single exec > departmental
  strat_6: skip('pick-list'),
  strat_7: skip('unvalenced'), // a longer investment horizon is not better or worse
  strat_8: skip('not-a-capability'), // competitive pressure is a fact about the market

  strat_9: skip('pick-list'),

  // ---- Data & Analytics Capabilities ----
  cap_1: asc(),
  cap_2: asc('borderline: multi-cloud may be a different choice rather than a better one'),
  cap_3: asc(),
  cap_4: asc(),
  cap_5: asc(),
  cap_6: asc('a percentage band, so ordinal and valenced'),
  cap_7: asc(),
  cap_8: asc(),
  cap_9: asc(),
  cap_10: asc(),
  cap_11: asc(),
  cap_12: asc(),
  cap_13: asc(),
  cap_14: skip('trend-not-level'), // rising spend on weak analytics is not capability

  // ---- Data Readiness & Governance ----
  ready_1: asc(),
  ready_2: asc(),
  ready_3: asc(),
  ready_4: asc(),
  ready_5: asc(),
  ready_6: asc(),
  ready_7: asc(),
  ready_8: asc(),
  ready_9: asc(),
  ready_10: asc(),
  ready_11: asc(),
  ready_12: asc(),
  ready_13: asc(),
  ready_14: skip('pick-list'), // hybrid is not better than a centralized CoE
};

/**
 * Whether a module produces a self-reported score at all.
 *
 * STRATEGIC PRIORITIES DOES NOT. Six of its nine questions are pick-lists, and the three
 * that survive measure importance, ambition and sponsorship rather than rating the seven
 * measures the module binds. strat_1's options ARE those measures: the module picks among
 * them, it does not rate them. A single number applied to all seven would be a value the
 * survey never produced, which is the exact defect this file was written to remove.
 *
 * Its measures stay INFORMED. They keep the survey tier and their coverage counts; they
 * simply have nothing to compare a document against, so they never produce an override.
 */
export const MODULE_POLICY: Readonly<Record<DemoSurveyModuleId, { scored: boolean }>> = {
  strategy: { scored: false },
  capabilities: { scored: true },
  readiness: { scored: true },
};

const round1 = (n: number) => Math.round(n * 10) / 10;

/** One question's score on the 1 to 5 scale, or null when policy excludes it. */
export function scoreQuestion(question: DemoSurveyQuestion): number | null {
  const policy = QUESTION_POLICY[question.question_id];
  if (!policy || !policy.score) return null;

  const options = question.options ?? [];
  if (options.length < 2) return null;
  if (Array.isArray(question.answer)) return null;

  const index = options.indexOf(question.answer);
  if (index === -1) return null;

  const effective = policy.orientation === 'descending' ? options.length - 1 - index : index;
  return 1 + 4 * (effective / (options.length - 1));
}

export interface ModuleScore {
  moduleId: DemoSurveyModuleId;
  /** Null when the module is unscorable, or when no question in it produced a value. */
  score: number | null;
  /** Question ids that contributed. Empty on an unscored module. */
  contributingQuestionIds: string[];
  /** Question ids excluded, with the reason. */
  excluded: { questionId: string; reason: ExclusionReason }[];
}

/** Derive one module's self-reported score from its seeded answers. */
export function scoreModule(moduleId: DemoSurveyModuleId): ModuleScore {
  const questions = DEMO_SURVEY_QUESTIONS[moduleId] ?? [];
  const excluded: ModuleScore['excluded'] = [];
  const contributingQuestionIds: string[] = [];
  const values: number[] = [];

  questions.forEach((q) => {
    const value = scoreQuestion(q);
    if (value === null) {
      const policy = QUESTION_POLICY[q.question_id];
      excluded.push({
        questionId: q.question_id,
        reason: policy && !policy.score ? policy.reason : 'pick-list',
      });
      return;
    }
    contributingQuestionIds.push(q.question_id);
    values.push(value);
  });

  if (!MODULE_POLICY[moduleId].scored) {
    return {
      moduleId,
      score: null,
      contributingQuestionIds: [],
      excluded: questions.map((q) => ({ questionId: q.question_id, reason: 'module-unscored' })),
    };
  }

  return {
    moduleId,
    score: values.length ? round1(values.reduce((a, b) => a + b, 0) / values.length) : null,
    contributingQuestionIds,
    excluded,
  };
}

/** Every module's score, computed once. */
export const MODULE_SCORES: Readonly<Record<DemoSurveyModuleId, ModuleScore>> = {
  strategy: scoreModule('strategy'),
  capabilities: scoreModule('capabilities'),
  readiness: scoreModule('readiness'),
};

/** The self-reported value a module carries, or null when it has none. */
export function selfReportedScore(moduleId: DemoSurveyModuleId): number | null {
  return MODULE_SCORES[moduleId].score;
}
